import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import is from '@sindresorhus/is';
import type { FunctionComponent, ReactNode } from 'react';
import { useContext } from 'react';
import Maybe from 'true-myth/maybe';

import { ApplicationContext } from '~/shared/context/context';

import { AvatarLarge, AvatarLargeAnonymized } from '../../../../design/components/avatar/AvatarLarge';
import { Headline } from '../../../../design/components/Typography';
import { getUserName } from '../../../common/user';
import { useOneUserQuery } from '../../../query-hooks/users';
import { GeneralInformationActionButtons } from './GeneralInformationActionButtons';

const StyledContainer = styled(Card)(({ theme }) => ({
    borderColor: theme.palette.cardborder.main,
    borderStyle: 'solid',
    borderWidth: 1,
    overflow: 'visible',
    width: '100%',
}));

type GeneralInformationProps = {
    readonly actionButtons?: ReactNode;
    readonly hideActionButtons?: boolean;
    readonly userId: string;
};

export const GeneralInformation: FunctionComponent<GeneralInformationProps> = ({
    actionButtons,
    hideActionButtons,
    userId,
}) => {
    const { userQuery } = useOneUserQuery(userId);
    const { applicationState } = useContext(ApplicationContext);
    const { myUserProfile, userIsAnonymized } = applicationState;
    const isCurrentUsersProfile = myUserProfile.get('id').mapOr(false, (myUserProfileId) => {
        return myUserProfileId === userId;
    });
    const isAnonymizationActive = userIsAnonymized && !isCurrentUsersProfile;

    const containerPadding = hideActionButtons === true ? 4 : 2;

    if (is.undefined(userQuery.data)) {
        return null;
    }

    const profileName = getUserName(Maybe.of(userQuery.data), isAnonymizationActive);

    const profileAvatar = isAnonymizationActive ? (
        <AvatarLargeAnonymized />
    ) : (
        <AvatarLarge firstName={userQuery.data.firstName ?? ''} lastName={userQuery.data.lastName ?? ''} />
    );

    return (
        <StyledContainer elevation={0}>
            <Box id="general-information" sx={{ position: 'relative', top: -100 }} />
            <Grid
                alignItems="center"
                container={true}
                direction="column"
                gap={1}
                justifyContent="center"
                p={containerPadding}
            >
                <Grid sx={{ mt: '-3.5rem' }}>{profileAvatar}</Grid>
                <Grid>
                    <Headline variant="h3">{profileName}</Headline>
                </Grid>
                <GeneralInformationActionButtons
                    actionButtons={actionButtons}
                    emailAddress={userQuery.data.emailAddress}
                    hideActionButtons={hideActionButtons}
                />
            </Grid>
        </StyledContainer>
    );
};
